import * as React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import Button from '@mui/material/Button'
import Popover from '@mui/material/Popover'
import { useTheme } from '@mui/material/styles'

const HintDot = ({ hint, targetId, position, onHintClose }) => {
    const theme = useTheme()
    const [anchorEl, setAnchorEl] = React.useState(null)
    const [dotPosition, setDotPosition] = React.useState({ top: 0, left: 0 })
    const isDarkTheme = theme.palette.mode === 'dark'
    const dotRef = React.useRef(null)

    const calculatePosition = React.useCallback(() => {
        const targetElement = document.getElementById(targetId)
        if (!targetElement) return

        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        const targetRect = targetElement.getBoundingClientRect()

        // Calculate position based on percentages
        const left = targetRect
            ? Math.floor(
                  targetRect.left + targetRect.width * (position.x / 100)
              )
            : Math.floor((position.x / 100) * viewportWidth)
        const top = targetRect
            ? Math.floor(
                  targetRect.top + targetRect.height * (position.y / 100)
              )
            : Math.floor((position.y / 100) * viewportHeight)

        // Adjust position relative to target element if needed
        const adjustedLeft = left
        const adjustedTop = top

        setDotPosition({ top: adjustedTop, left: adjustedLeft })
    }, [targetId, position])

    React.useEffect(() => {
        calculatePosition()
        window.addEventListener('resize', calculatePosition)
        return () => window.removeEventListener('resize', calculatePosition)
    }, [calculatePosition])

    const handleClick = event => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
        onHintClose()
    }

    const open = Boolean(anchorEl)

    // Determine popover placement based on screen position
    const getPopoverPlacement = () => {
        const centerX = window.innerWidth / 2
        const centerY = window.innerHeight / 2
        return {
            vertical: dotPosition.top > centerY ? 'top' : 'bottom',
            horizontal: dotPosition.left > centerX ? 'right' : 'left',
        }
    }

    const placement = getPopoverPlacement()

    return (
        <Box
            ref={dotRef}
            sx={{
                position: 'fixed',
                top: dotPosition.top,
                left: dotPosition.left,
                zIndex: theme.zIndex.tooltip,
            }}
        >
            <Box
                onClick={handleClick}
                sx={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: isDarkTheme
                        ? 'radial-gradient(circle, rgba(0,123,255,0.95) 30%, rgba(0,123,255,0.6) 60%, rgba(0,123,255,0.2) 100%)'
                        : 'radial-gradient(circle, rgba(0,123, 255,0.8) 30%, rgba(0,123, 255,0.4) 60%, rgba(0,123, 255,0.1) 100%)',
                    cursor: 'pointer',
                    position: 'relative',
                    transform: 'translate(-50%, -50%)',
                    boxShadow: isDarkTheme
                        ? '0 0 10px rgba(0,123,255, 0.5), 0 0 20px rgba(0,123,255, 0.3), inset 0 0 8px rgba(0,123,255, 0)'
                        : '0 0 10px rgba(0, 123, 255, 0.3), 0 0 20px rgba(0, 123, 255, 0.2), inset 0 0 8px rgba(0, 123, 255, 0)',
                    backdropFilter: 'blur(0.5px)',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: '24px',
                        height: '24px',
                        transform: 'translate(-50%, -50%)',
                        borderRadius: '50%',
                        background: isDarkTheme
                            ? 'radial-gradient(circle, rgba(0,123,255,0.2) 0%, rgba(0,123,255,0.1) 50%, rgba(0,123,255,0) 100%)'
                            : 'radial-gradient(circle, rgba(0,123,255,0.4) 0%, rgba(0,123,255,0.2) 50%, rgba(0,123,255,0) 100%)',
                        animation: 'pulse 1s ease-in-out infinite',
                        filter: 'blur(1px)',
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: '24px',
                        height: '24px',
                        transform: 'translate(-50%, -50%)',
                        borderRadius: '50%',
                        border: `8px solid ${
                            isDarkTheme
                                ? 'rgba(0,123,255, 0.4)'
                                : 'rgba(0, 123, 255, 0.3)'
                        }`,
                        animation: 'glimmer 1s ease-in-out infinite',
                        filter: 'blur(0.5px)',
                    },
                    '@keyframes pulse': {
                        '0%': {
                            transform: 'translate(-50%, -50%) scale(1)',
                            opacity: 0.8,
                        },
                        '50%': {
                            transform: 'translate(-50%, -50%) scale(1.5)',
                            opacity: 0.2,
                        },
                        '100%': {
                            transform: 'translate(-50%, -50%) scale(1)',
                            opacity: 0.8,
                        },
                    },
                    '@keyframes glimmer': {
                        '0%': {
                            transform:
                                'translate(-50%, -50%) scale(1) rotate(0deg)',
                            opacity: 1,
                        },
                        '50%': {
                            transform:
                                'translate(-50%, -50%) scale(1.2) rotate(180deg)',
                            opacity: 0.4,
                        },
                        '100%': {
                            transform:
                                'translate(-50%, -50%) scale(1) rotate(360deg)',
                            opacity: 1,
                        },
                    },
                }}
            />

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: placement.vertical,
                    horizontal: placement.horizontal,
                }}
                transformOrigin={{
                    vertical: placement.vertical === 'top' ? 'bottom' : 'top',
                    horizontal:
                        placement.horizontal === 'right' ? 'left' : 'right',
                }}
                PaperProps={{
                    sx: {
                        p: 2,
                        maxWidth: 300,
                        borderRadius: 2,
                        boxShadow: theme => theme.shadows[3],
                        backgroundColor: theme =>
                            theme.palette.background.paper,
                    },
                }}
            >
                {/* <Box sx={{ position: 'relative', mb: 1 }}>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            right: -8,
                            top: -8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box> */}
                <Typography variant="body2" sx={{ pt: 1, pl: 1, pr: 2, pb: 1 }}>
                    {hint}
                </Typography>
                <Box
                    sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleClose}
                        size="small"
                    >
                        Okay
                    </Button>
                </Box>
            </Popover>
        </Box>
    )
}

export default HintDot
